import { ChevronRight, RotateCcw, Youtube } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import useTranslation from "../../../client/hooks/useTranslation";
import { getMoodEmoji, getMoodLabel, getPerformanceLabel } from "../../../trading-sessions/sessionUtils";
import { SessionErrorBadges } from "../../../trading-sessions/SessionErrorBadges";
import { useLiteData } from "../../data/LiteDataProvider";
import { Select } from "../../ui/Select";
import { Empty, Pnl, cardCls } from "../../ui/ui";
import { Chip, MoodChip, PerfChip, useDurationLabel } from "./SessionBits";
import { MOODS, PERFORMANCES, durationMinutes, type SessionRow } from "./sessionData";

const PAGE = 12;

/**
 * Historique des sessions terminées, avec les filtres de la table Advanced
 * (humeur, performance, durée min / max). La période et le périmètre viennent
 * de la barre de filtres commune.
 */
export function SessionHistory({ rows }: { rows: SessionRow[] }) {
  const t = useTranslation();
  const tl = t.lite.sessions.history;
  const [mood, setMood] = useState<string | null>(null);
  const [perf, setPerf] = useState<string | null>(null);
  const [minDur, setMinDur] = useState("");
  const [maxDur, setMaxDur] = useState("");
  const [shown, setShown] = useState(PAGE);

  const filtered = useMemo(() => rows.filter(({ session: s }) => {
    if (mood && s.mood !== mood) return false;
    if (perf && s.performance !== perf) return false;
    // Même règle que la table Advanced : sur la durée enregistrée.
    if (minDur && (s.actualDuration ?? 0) < parseInt(minDur)) return false;
    if (maxDur && (s.actualDuration ?? 0) > parseInt(maxDur)) return false;
    return true;
  }), [rows, mood, perf, minDur, maxDur]);

  const hasFilters = !!mood || !!perf || minDur !== "" || maxDur !== "";
  const reset = () => { setMood(null); setPerf(null); setMinDur(""); setMaxDur(""); };
  const durInput = "h-9 w-16 rounded-xl border border-border bg-card px-2 text-sm font-mono text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/40";

  return (
    <section className={`${cardCls} overflow-hidden`}>
      <header className="px-4 md:px-5 pt-4 pb-3 border-b border-border space-y-3">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-sm font-semibold">{tl.title}</h2>
          <span className="text-xs text-muted-foreground">{tl.count(filtered.length)}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select<string>
            label={t.sessions.filterMood}
            allLabel={t.sessions.allMoods}
            value={mood}
            options={MOODS.map(m => ({ value: m, label: `${getMoodEmoji(m)} ${getMoodLabel(m, t)}` }))}
            onChange={v => { setMood(v); setShown(PAGE); }}
          />
          <Select<string>
            label={t.sessions.filterPerformance}
            allLabel={t.sessions.allPerformances}
            value={perf}
            options={PERFORMANCES.map(p => ({ value: p, label: getPerformanceLabel(p, t) }))}
            onChange={v => { setPerf(v); setShown(PAGE); }}
          />
          <div className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <span>{tl.duration}</span>
            <input type="number" min={0} inputMode="numeric" className={durInput} placeholder={tl.durationMin} value={minDur} onChange={e => setMinDur(e.target.value)} aria-label={t.sessions.filterDurationMin} title={t.sessions.filterDurationMin} />
            <span>-</span>
            <input type="number" min={0} inputMode="numeric" className={durInput} placeholder={tl.durationMax} value={maxDur} onChange={e => setMaxDur(e.target.value)} aria-label={t.sessions.filterDurationMax} title={t.sessions.filterDurationMax} />
            <span className="text-xs">{tl.minutesUnit}</span>
          </div>
          {hasFilters && (
            <button type="button" onClick={reset} className="h-9 px-2.5 inline-flex items-center gap-2 rounded-xl text-sm text-muted-foreground hover:text-foreground hover:bg-muted" title={t.sessions.filterReset}>
              <RotateCcw size={14} /><span className="hidden sm:inline">{t.lite.filters.reset}</span>
            </button>
          )}
        </div>
      </header>

      {filtered.length === 0 ? (
        <Empty title={t.sessions.noSessionsFiltered} />
      ) : (
        <div className="divide-y divide-border">
          {filtered.slice(0, shown).map(r => <SessionLine key={r.session.id} row={r} />)}
        </div>
      )}

      {shown < filtered.length && (
        <div className="flex justify-center px-4 py-3 border-t border-border">
          <button type="button" className="h-9 px-4 rounded-xl border border-border text-sm hover:bg-muted" onClick={() => setShown(s => s + PAGE)}>
            {tl.showMore(filtered.length - shown)}
          </button>
        </div>
      )}
    </section>
  );
}

function SessionLine({ row }: { row: SessionRow }) {
  const t = useTranslation();
  const tl = t.lite.sessions.history;
  const { fmt } = useLiteData();
  const duration = useDurationLabel();
  const s = row.session;
  const start = new Date(s.startedAt!);
  const accountCount = new Set(row.trades.map(tr => tr.accountId).filter(Boolean)).size;

  return (
    <Link to={`/session/${s.id}`} className="group flex items-center gap-3 md:gap-4 px-4 md:px-5 py-3.5 hover:bg-muted/60 transition-colors">
      {/* Date : le repère principal d'une session. */}
      <div className="w-12 shrink-0 text-center">
        <div className="font-display text-xl font-bold leading-none">{start.getDate()}</div>
        <div className="text-[11px] text-muted-foreground mt-1 uppercase">{fmt.monthShort(start)}</div>
      </div>

      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`truncate font-medium ${s.name ? "text-foreground" : "text-muted-foreground"}`}>{s.name || fmt.dateLong(start)}</span>
          {s.isAutoGenerated && <Chip className="bg-primary/10 text-primary">{t.sessions.autoBadge}</Chip>}
          {s.videoUrl && <Youtube size={15} className="text-primary shrink-0" aria-label={t.sessions.colVideo} />}
        </div>
        <div className="flex items-center gap-x-2 gap-y-1 flex-wrap text-xs text-muted-foreground">
          <span className="font-mono">{fmt.time(start.toISOString())}</span>
          <span>·</span>
          <span>{duration(durationMinutes(s))}</span>
          <span>·</span>
          <span>{row.trades.length ? t.lite.common.trades(row.trades.length) : tl.noTradesInSession}</span>
          {accountCount > 1 && (
            <>
              <span>·</span>
              <span>{tl.accountsCount(accountCount)}</span>
            </>
          )}
          <MoodChip mood={s.mood} />
          <PerfChip performance={s.performance} />
        </div>
        <SessionErrorBadges errors={s.errors} />
      </div>

      <div className="shrink-0 text-right">
        {row.trades.length ? <Pnl value={row.pnl} decimals className="font-semibold" /> : <span className="text-sm text-muted-foreground">-</span>}
        {(s.maxRunup != null || s.maxDrawdown != null) && (
          <div className="hidden sm:block text-[11px] font-mono text-muted-foreground mt-1 space-x-2">
            {s.maxRunup != null && <span title={t.sessions.colRunup}><span className="text-pnlPositive">▲</span> {fmt.money(s.maxRunup, { sign: false })}</span>}
            {s.maxDrawdown != null && <span title={t.sessions.colDrawdown}><span className="text-pnlNegative">▼</span> {fmt.money(Math.abs(s.maxDrawdown), { sign: false })}</span>}
          </div>
        )}
      </div>
      <ChevronRight size={16} className="hidden sm:block shrink-0 text-muted-foreground/60 group-hover:text-primary transition-colors" />
    </Link>
  );
}
